import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import CustomLoginDialog from './Login/CustomLoginDialog';

// Importamos íconos mejorados
import {
  FaSignInAlt,
  FaSchool,
  FaGlobe,
  FaSearch,
  FaYoutube,
  FaCalendarAlt,
  FaWpforms,
  FaFileAlt,
  FaStickyNote
} from 'react-icons/fa';
import {
  SiGmail,
  SiGoogleclassroom,
  SiGoogledrive,
  SiGooglesheets,
  SiGoogleslides,
  SiCanva,
  SiGooglemeet
} from 'react-icons/si';
import {
  MdOutlineVideoLibrary
} from 'react-icons/md';

// Importamos el CSS modular
import styles from './Inicio.module.css';

const Inicio = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openLoginDialog, setOpenLoginDialog] = useState(false);
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);

  // Manejar cambio en la barra de búsqueda
  const handleSearchChange = useCallback((event) => {
    setSearchQuery(event.target.value);
  }, []);

  // Manejar envío de búsqueda
  const handleSearchSubmit = useCallback((e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.open(`https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`, '_blank');
    }
  }, [searchQuery]);

  // Se invoca cuando el login es exitoso
  const handleLoginSuccess = useCallback((userData) => {
    console.log('Login exitoso!', userData);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setOpenLoginDialog(false);
    navigate('/inicio');
  }, [setUser, navigate]);

  // Datos de servicios memoizados con iconos mejorados
  const services = useMemo(() => [
    { name: 'Canva', icon: SiCanva, color: '#00C4CC', url: 'https://www.canva.com/es_es/login/', appScheme: null }, // Canva usa web URLs
    { name: 'Gmail', icon: SiGmail, color: '#EA4335', url: 'https://mail.google.com', appScheme: 'googlegmail://' },
    { name: 'Classroom', icon: SiGoogleclassroom, color: '#0F9D58', url: 'https://classroom.google.com', appScheme: null }, // Classroom usa web URLs
    { name: 'Drive', icon: SiGoogledrive, color: '#FBBC04', url: 'https://drive.google.com', appScheme: 'googledrive://' },
    { name: 'YouTube', icon: FaYoutube, color: '#FF0000', url: 'https://www.youtube.com', appScheme: 'youtube://' },
    { name: 'Calendar', icon: FaCalendarAlt, color: '#1967D2', url: 'https://calendar.google.com', appScheme: 'googlecalendar://' },
    { name: 'Meet', icon: SiGooglemeet, color: '#00897B', url: 'https://meet.google.com', appScheme: 'comgooglemeet://' },
    { name: 'Vids', icon: MdOutlineVideoLibrary, color: '#7C3AED', url: 'https://vids.google.com', appScheme: null } // Vids usa web URLs
  ], []);

  // Componente mejorado para los iconos de servicios Google y Canva
  const GoogleServiceIcons = React.memo(() => (
    <div className={styles.iconSection}>
      <hr className={styles.divider} />
      <div className={styles.iconGrid}>
        {services.map((service) => {
          const IconComponent = service.icon;
          // Solo Canva no necesita el contenedor circular blanco
          const noBgCircle = service.name === 'Canva';

          return (
            <a
              key={service.name}
              href={service.url}
              onClick={(e) => handleAppClick(e, service.appScheme, service.url)}
              className={styles.iconLink}
              aria-label={`Ir a ${service.name}`}
            >
              <div className={`${styles.icon} ${noBgCircle ? styles.iconNoBg : ''}`}>
                <IconComponent size={noBgCircle ? 42 : 22} color={service.color} />
              </div>
              <span className={styles.iconLabel}>{service.name}</span>
            </a>
          );
        })}
      </div>
      <hr className={styles.divider} />
    </div>
  ));

  // Datos de apps de Google Docs memoizados
  const googleApps = useMemo(() => [
    { name: 'Docs', icon: FaFileAlt, bgColor: '#4285F4', url: 'https://docs.google.com/document', appScheme: 'googledocs://' },
    { name: 'Sheets', icon: SiGooglesheets, bgColor: '#0F9D58', url: 'https://docs.google.com/spreadsheets', appScheme: 'googlesheets://' },
    { name: 'Slides', icon: SiGoogleslides, bgColor: '#F4B400', url: 'https://docs.google.com/presentation', appScheme: 'googleslides://' },
    { name: 'Forms', icon: FaWpforms, bgColor: '#673AB7', url: 'https://docs.google.com/forms', appScheme: null }, // Forms usa web URLs
    { name: 'Sites', icon: FaGlobe, bgColor: '#4285F4', url: 'https://sites.google.com', appScheme: null }, // Sites usa web URLs
    { name: 'Keep', icon: FaStickyNote, bgColor: '#FBBC04', url: 'https://keep.google.com', appScheme: 'comgooglekeep://' }
  ], []);

  // Componente mejorado para los botones de documentos de Google
  const GoogleDocsButtons = React.memo(() => (
    <div className={styles.docGrid}>
      {googleApps.map((app) => {
        const IconComponent = app.icon;
        return (
          <div key={app.name} className={styles.docItem}>
            <a
              href={app.url}
              onClick={(e) => handleAppClick(e, app.appScheme, app.url)}
              className={styles.docButton}
              aria-label={`Abrir ${app.name}`}
            >
              <div className={styles.docButtonIcon}>
                <div style={{
                  width: 36,
                  height: 36,
                  backgroundColor: app.bgColor,
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <IconComponent size={20} color="white" />
                </div>
              </div>
            </a>
            <span className={styles.docLabel}>{app.name}</span>
          </div>
        );
      })}
    </div>
  ));

  // Función para intentar abrir app nativa o web
  const handleAppClick = useCallback((e, appScheme, webUrl) => {
    e.preventDefault();

    // Si no hay appScheme, abre directamente la URL web
    // (algunos servicios como Classroom, Canva usan Universal Links)
    if (!appScheme) {
      window.open(webUrl, '_blank');
      return;
    }

    // Para móvil/tablet: intenta abrir app nativa primero
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) {
      // Crea un iframe oculto para intentar abrir el esquema de la app
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = appScheme;
      document.body.appendChild(iframe);

      // Timeout para abrir la web si la app no está instalada
      const timeout = setTimeout(() => {
        document.body.removeChild(iframe);
        window.location.href = webUrl;
      }, 1500);

      // Si la página pierde el foco, significa que la app se abrió
      const handleVisibilityChange = () => {
        if (document.hidden) {
          clearTimeout(timeout);
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 1000);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Limpieza después de 3 segundos
      setTimeout(() => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }, 3000);
    } else {
      // En desktop, abre directamente la URL web
      window.open(webUrl, '_blank');
    }
  }, []);

  // Datos de aplicaciones del colegio memoizados
  const schoolApps = useMemo(() => [
    { name: 'Pictoescritura', webUrl: 'https://aula.pictoescritura.com/', appScheme: null, logo: '/logos/pictoescritura.png', hasWhiteBg: false },
    { name: 'Edelvives', webUrl: 'https://edelvivesdigital.com/', appScheme: null, logo: '/logos/edelvives.png', hasWhiteBg: false },
    { name: 'Aula Virtual', webUrl: 'https://av.santillana.es/av/login/choice', appScheme: null, logo: '/logos/santillana.png', hasWhiteBg: true },
    { name: 'Blink Learning', webUrl: 'https://www.blinklearning.com/v/1761838945/themes/tmpux/launch.php', appScheme: 'blinklearning://', logo: '/logos/blink.png', hasWhiteBg: false },
    { name: 'Burlington English', webUrl: 'https://app.burlingtonenglish.com/', appScheme: null, logo: '/logos/burlington.png', hasWhiteBg: true },
    { name: 'Lazarus', webUrl: 'https://colegiotrueba.imtlazarus.com/lazarus/access.php', appScheme: null, logo: '/logos/lazarus.png', hasWhiteBg: true }
  ], []);

  // Componente compacto para las aplicaciones del colegio
  const SchoolAppsCompact = React.memo(() => (
    <div className={styles.schoolAppsCompact}>
      <h3 className={styles.schoolAppsTitle}>Aplicaciones del Colegio</h3>
      <div className={styles.schoolAppsRow}>
        {schoolApps.map((app) => {
          return (
            <div key={app.name} className={styles.schoolAppItem}>
              <a
                href={app.webUrl}
                onClick={(e) => handleAppClick(e, app.appScheme, app.webUrl)}
                className={`${styles.schoolAppCompactButton} ${!app.hasWhiteBg ? styles.schoolAppNoBg : ''}`}
                aria-label={`Abrir ${app.name}`}
              >
                <img
                  src={app.logo}
                  alt={app.name}
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = styles.appFallbackIcon;
                    fallback.textContent = app.name.substring(0, 2).toUpperCase();
                    e.target.parentElement.appendChild(fallback);
                  }}
                />
              </a>
              <span className={styles.schoolAppLabel}>{app.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  ));

  return (
    <div className={`${styles.homepage} ${user ? styles.loggedIn : ''}`}>


      <header className={styles.header}>
        <img
          src="/Background.png"
          alt="Header Background"
          className={styles.headerImage}
        />

        {!user && (
          <nav className={styles.navbar}>
            <a
              href="https://www.colegiotrueba.net"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.navbarLogo}
            >
              <img src="/logotrueba.png" alt="Trueba" />
            </a>
            <button
              className={styles.loginButton}
              onClick={() => setOpenLoginDialog(true)}
            >
              <FaSignInAlt className={styles.loginIcon} /> LOGIN
            </button>
          </nav>
        )}

        <div className={styles.headerText}>
          <h1>TRUEBA VIRTUAL</h1>
        </div>
      </header>

      {!user && (
        <CustomLoginDialog
          open={openLoginDialog}
          onClose={() => setOpenLoginDialog(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      )}

      <main className={styles.container}>
        {/* Sección de búsqueda centrada */}
        <section className={styles.searchSection}>
          <img src="/Google.png" alt="Google Logo" className={styles.googleLogo} />
          <form className={styles.searchBarWrapper} onSubmit={handleSearchSubmit}>
            <input
              type="text"
              placeholder="Buscar en Google..."
              className={styles.searchInput}
              value={searchQuery}
              onChange={handleSearchChange}
              aria-label="Búsqueda de Google"
            />
            <button type="submit" className={styles.searchButton} aria-label="Buscar">
              <FaSearch /> BUSCAR
            </button>
          </form>
        </section>

        {/* Iconos de servicios Google */}
        <GoogleServiceIcons />

        {/* Layout original con botones laterales, calendario y docs */}
        <div className={styles.contentGrid}>
          <div className={styles.leftColumn}>
            <div className={styles.sideButtons}>
              <a
                href="https://web2.alexiaedu.com/ACWeb/LogOn.aspx?key=pMC8SfW%2bN%2fE%3d"
                target="_blank"
                rel="noopener noreferrer"
                className={`${styles.sideButton} ${styles.alexiaButton}`}
                aria-label="Acceder a Alexia"
              >
                <FaSchool className={styles.sideButtonIcon} />
                <span>ALEXIA</span>
              </a>

              <a
                href="http://www.colegiotrueba.net/"
                target="_blank"
                rel="noopener noreferrer"
                className={`${styles.sideButton} ${styles.webButton}`}
                aria-label="Visitar web del colegio"
              >
                <FaGlobe className={styles.sideButtonIcon} />
                <span>WEB</span>
              </a>
            </div>

            {/* Apps del colegio debajo de los botones */}
            <SchoolAppsCompact />
          </div>

          <div className={styles.rightContent}>
            <div className={styles.calendarContainer}>
              <iframe
                src="https://calendar.google.com/calendar/embed?height=600&wkst=2&ctz=Europe%2FMadrid&showPrint=0&showTz=0&showTitle=0&src=Y29sZWdpb3RydWViYS5uZXRfYXRiOHNhNTZkZGs0aDVpaGNiYzJncm4xZHNAZ3JvdXAuY2FsZW5kYXIuZ29vZ2xlLmNvbQ&src=Y185ZTE1ZTRlNDhiODFhMDI3OTQ4MGEyMjdjZDcxZWNmNWM5NGUzODdmMTY4ZTI0OWQ4NTJjZWZjYWJlODdjN2FmQGdyb3VwLmNhbGVuZGFyLmdvb2dsZS5jb20&src=Y29sZWdpb3RydWViYS5uZXRfcWxmYnVjNjlrYjUxZWozbnBrcmI4b2kwY2NAZ3JvdXAuY2FsZW5kYXIuZ29vZ2xlLmNvbQ&src=Y29sZWdpb3RydWViYS5uZXRfZzZhMzg3NXNqcnVxcGZqaGhxMmZpbjdhMnNAZ3JvdXAuY2FsZW5kYXIuZ29vZ2xlLmNvbQ&src=Y29sZWdpb3RydWViYS5uZXRfaTljNWc4ajNobzdtNW9hMmloZ2hyYTcyb29AZ3JvdXAuY2FsZW5kYXIuZ29vZ2xlLmNvbQ&src=Y29sZWdpb3RydWViYS5uZXRfdGp0dnZzdWg5amdzaXFrMjEzM2M3ZzY4czRAZ3JvdXAuY2FsZW5kYXIuZ29vZ2xlLmNvbQ&src=Y19hNGIyNGFlMDQ1MzM0MmM5NDc1NmRkYTI3ZGQ2OTBhNDU5MjNkYjU0YTZmM2YxZGY0MzQ2YWMxZjYxNTJlYzQwQGdyb3VwLmNhbGVuZGFyLmdvb2dsZS5jb20&src=Y18xN2M1MDYxYWQ4ODQ1NmEzYjhiMDU2ZDAzODQwNjFiNmUwYjc3ZDQxNzc0N2YxNmNjMWYzMmFlZDhmZGZjYzhkQGdyb3VwLmNhbGVuZGFyLmdvb2dsZS5jb20&src=Y29sZWdpb3RydWViYS5uZXRfZjZuNXBoNXNtZmwzazQ4djFuY3F1dTJwbmtAZ3JvdXAuY2FsZW5kYXIuZ29vZ2xlLmNvbQ&color=%23a7715e&color=%23b39ddb&color=%23e4c441&color=%234285f4&color=%23d50000&color=%237cb342&color=%23039be5&color=%23616161&color=%233f51b5"
                className={styles.calendarFrame}
                title="Calendario Escolar"
                loading="lazy"
              />
            </div>

            <GoogleDocsButtons />
          </div>
        </div>
      </main>

      {!user && (
        <footer className={styles.footer}>
          <div className={styles.footerContent}>
            <p>&copy; {new Date().getFullYear()} Colegio Trueba - Trueba Virtual. Todos los derechos reservados.</p>
          </div>
        </footer>
      )}
    </div>
  );
};

export default Inicio;
